import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@platform/ui-kit';
import {
  api,
  type SpnCaseLevel,
  type SpnChallenge,
  type SpnDimension,
  type SpnDimensionKey,
  type SpnDiscoveryOpportunity,
  type SpnDiscoveryRound,
  type SpnDiscoverySessionDetail,
} from '../../../api';
import { HelpPanel, type Help } from './HelpPanel';
import './DiscoverySession.css';

/**
 * Strategic Portfolio Navigator → Discoveries → one session.
 *
 * BUILT FROM THE PROTOTYPE the owner approved on 2026-08-04 (v4), after three earlier
 * layouts were rejected. What each rejection taught, so it is not undone later:
 *
 *   * A flat table with every detail on screen is a wall, not a table. Left panel is the
 *     list; right panel is whichever lens you open.
 *   * Every dimension shows LOW / MEDIUM / HIGH — three scopes of the same opportunity, not
 *     an error bar. Medium is the recommendation and the default, and the client can carry
 *     any of the three on any dimension.
 *   * Colour is a light wash behind ONE value per tile, not a traffic light on everything.
 *   * The chosen row is marked by an inset shadow, never padding — padding shifts that row's
 *     columns out of line with the two beside it.
 *   * Timeline is a Gantt across AI Readiness' five delivery phases, three cases on one
 *     shared axis so the difference in length reads directly.
 *
 * SELECTION, NOT KEEP/DISCARD. A ticked opportunity is one the client is carrying into a
 * Word or PowerPoint document — a stronger statement than "not discarded", and the only
 * thing the exports read.
 */

const POLL_MS = 4000;

/**
 * Help content, in APR's house style: `purpose` says what the screen is FOR rather than
 * what it contains; `doHere` is imperatives with the affordances bolded, never numbered;
 * `fits` is the breadcrumb; `firstTime` is one sentence. Token cost is called out where
 * an action spends it, as APR does.
 */
const SESSION_HELP: Help = {
  title: 'Strategic Discoveries',
  icon: '🔭',
  purpose:
    'Review what the analysis found, decide what you are carrying forward, and argue with anything you think is wrong. Every opportunity carries a <b>low</b>, a <b>medium</b> and a <b>high</b> case on five dimensions — these are three scopes of the same work, not an error bar, and medium is the recommendation.',
  doHere: [
    'Click a metric tile on the left — <b>Benefit</b>, <b>ROI</b>, <b>Risk</b>, <b>Cost</b>, <b>Timeline</b> — to open that dimension in the right panel.',
    'Pick the case you are carrying on any dimension. The tally, the tiles and the exports all follow your choice.',
    '<b>Tick</b> an opportunity to include it in the Word or PowerPoint output. Nothing else is exported.',
    'Use <b>Edit &amp; challenge</b> to change any value and tell the model what you want tested, then <b>Re-run as a variant</b>.',
    '<b>Ask &amp; continue</b> at the bottom adds a round to the whole session — a follow-up question, or another business area.',
    '<b>Delete</b> on a row removes one opportunity; its variants go with it.',
  ],
  fits: 'Strategic Portfolio Navigator &rarr; Discoveries &rarr; a session',
  firstTime:
    'Open one opportunity and read its Benefit lens — three scopes with a justification each is the shape everything else on this screen follows.',
  sections: [
    {
      label: 'The three cases',
      items: [
        { term: 'Low', color: '#546e7a', def: 'The cautious scope — a defensible win and nothing more. The justification names what is deliberately left out.' },
        { term: 'Medium', color: '#1f6b38', def: 'The <b>recommendation</b>, and what a competent team would actually do. Selected by default.' },
        { term: 'High', color: '#8a5a12', def: 'The full scope, and what it additionally requires.' },
      ],
    },
    {
      label: 'Reading the timeline',
      items: [
        { term: 'Timeline', color: '#1565c0', def: '<b>Implementation effort</b> — the work to make the change, across the five delivery phases. Nothing else.' },
        { term: 'Gates', color: '#9a3412', def: 'A regulator, a renewal date, a counterparty. <b>Not</b> added to the timeline: it is not effort, and no one on the team can compress it.' },
      ],
      note: 'Payback is in the ROI lens. Three different clocks, deliberately kept apart — conflating them is what turns a four-month procurement change into a two-year programme.',
    },
    {
      label: 'Challenging a finding',
      items: [
        { term: 'Your edits', color: '#1f3a5f', def: 'Facts you are <b>asserting</b> — "our audit measured 0.18%".' },
        { term: 'The challenge', color: '#1f3a5f', def: 'The <b>argument</b> — what you believe and what you want checked.' },
        { term: 'The variant', color: '#1f6b38', def: 'What comes back, as a row <b>beside</b> the original. Both stay, so you can compare them.' },
      ],
      note: 'Agreement is not the goal. The model is told to hold its figure where you are asserting a preference rather than evidence, and to say so. A re-run spends tokens — roughly 30 seconds for one opportunity.',
    },
  ],
};

/** Icons — one per dimension, so the five are told apart by shape as well as by label. */
const ICON: Record<string, JSX.Element> = {
  desc: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M3 4h10M3 8h10M3 12h6" />
    </svg>
  ),
  benefit: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M2 13h12" />
      <path d="M4.5 13V9M8 13V5M11.5 13V7" />
    </svg>
  ),
  roi: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M13 8a5 5 0 1 1-1.8-3.85" />
      <path d="M13 2v3h-3" />
    </svg>
  ),
  risk: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M8 2l5 2v4c0 3-2.2 5.3-5 6-2.8-.7-5-3-5-6V4z" />
      <path d="M8 6v3M8 11h.01" />
    </svg>
  ),
  cost: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="2" y="4" width="12" height="8" rx="1" />
      <circle cx="8" cy="8" r="1.7" />
    </svg>
  ),
  timeline: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </svg>
  ),
};

/**
 * AI Readiness' five delivery phases and its 1/2/4/2/1 working-day ratio, from that
 * product's own `roadmapScheduler.js`. Reused rather than reinvented — the two products
 * disagreeing about how a programme is phased would be worse than either choice.
 */
const PHASES = [
  { name: 'Assessment & Planning', abbr: 'Assess', ratio: 1, colour: 'var(--ds-p1)' },
  { name: 'Design & Architecture', abbr: 'Design', ratio: 2, colour: 'var(--ds-p2)' },
  { name: 'Development', abbr: 'Build', ratio: 4, colour: 'var(--ds-p3)' },
  { name: 'Testing & Validation', abbr: 'Test', ratio: 2, colour: 'var(--ds-p4)' },
  { name: 'Go-Live & Optimization', abbr: 'Go-Live', ratio: 1, colour: 'var(--ds-p5)' },
] as const;

const RATIO_TOTAL = PHASES.reduce((a, p) => a + p.ratio, 0);

const METRICS: Array<{ key: SpnDimensionKey; label: string }> = [
  { key: 'benefit', label: 'Benefit' },
  { key: 'roi', label: 'ROI' },
  { key: 'risk', label: 'Risk' },
  { key: 'cost', label: 'Cost' },
  { key: 'timeline', label: 'Timeline' },
];

const LEVELS: SpnCaseLevel[] = ['low', 'medium', 'high'];

const TITLE: Record<SpnDimensionKey, string> = {
  benefit: 'Business benefit',
  roi: 'Return on investment',
  risk: 'Delivery risk',
  cost: 'Cost to deliver',
  timeline: 'Timeline',
};

const UNIT: Record<SpnDimensionKey, string> = {
  benefit: 'annual EBITDA impact',
  roi: 'payback period',
  risk: 'exposure',
  cost: 'cost to deliver',
  timeline: 'implementation effort',
};

const compact = (n: number) => {
  if (!Number.isFinite(n) || n === 0) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
};

/** The numeric worth of an opportunity under the case the client is carrying. */
function chosenValue(o: SpnDiscoveryOpportunity): number {
  const level = o.chosen?.benefit ?? 'medium';
  const raw = level === 'low' ? o.ebitda_low : level === 'high' ? o.ebitda_high : o.ebitda_impact;
  return Number(raw) || 0;
}

/**
 * The dimensions an opportunity can show.
 *
 * Opportunities produced before migration 0040 have no `cases` document, and rendering
 * them as five dashes makes a real analysis look empty. Where the older columns hold the
 * same information they are LIFTED into the three-case shape — benefit from the ebitda
 * range, risk from the confidence band, timeline from the recorded duration — with the
 * justification left blank rather than invented. Anything genuinely absent stays absent;
 * this fills nothing in.
 */
function casesOf(o: SpnDiscoveryOpportunity): Partial<Record<SpnDimensionKey, SpnDimension<string | number>>> {
  if (o.cases) return o.cases as Partial<Record<SpnDimensionKey, SpnDimension<string | number>>>;

  const out: Partial<Record<SpnDimensionKey, SpnDimension<string | number>>> = {};
  const money = (v: string | number | null) =>
    v == null ? null : compact(Number(v) || 0);

  if (o.ebitda_impact != null || o.ebitda_low != null || o.ebitda_high != null) {
    out.benefit = {
      tone: 'moderate',
      low: { value: money(o.ebitda_low), justification: '' },
      medium: { value: money(o.ebitda_impact), justification: '' },
      high: { value: money(o.ebitda_high), justification: '' },
    };
  }
  if (o.confidence) {
    // Confidence is not risk, but on a pre-0040 row it is the only band recorded, and the
    // lens says so rather than passing it off as a risk assessment.
    const band = o.confidence.toLowerCase();
    out.risk = {
      tone: 'moderate',
      low: { value: 'Low', justification: '' },
      medium: { value: band.charAt(0).toUpperCase() + band.slice(1), justification: '' },
      high: { value: 'High', justification: '' },
    };
  }
  if (o.time_to_impact) {
    const weeks = parseInt(o.time_to_impact, 10);
    if (Number.isFinite(weeks)) {
      out.timeline = {
        tone: 'moderate',
        low: { value: null, justification: '' },
        medium: { value: weeks, justification: '' },
        high: { value: null, justification: '' },
      };
    }
  }
  return out;
}

/** What a dimension displays at its chosen level — "$4.1m", "7 mo", "12 wk". */
function displayValue(o: SpnDiscoveryOpportunity, key: SpnDimensionKey): string {
  const dim = casesOf(o)[key];
  if (!dim) return '—';
  const level = o.chosen?.[key] ?? 'medium';
  const raw = dim[level]?.value;
  if (raw == null) return '—';
  return key === 'timeline' ? `${raw} wk` : String(raw);
}

function levelValue(dim: SpnDimension<string | number>, level: SpnCaseLevel, key: SpnDimensionKey) {
  const raw = dim[level]?.value;
  if (raw == null) return '—';
  return key === 'timeline' ? `${raw} wk` : String(raw);
}

export function DiscoverySession({ code }: { code: string }) {
  const navigate = useNavigate();

  const [session, setSession] = useState<SpnDiscoverySessionDetail | null>(null);
  const [opportunities, setOpportunities] = useState<SpnDiscoveryOpportunity[]>([]);
  const [rounds, setRounds] = useState<SpnDiscoveryRound[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const [openId, setOpenId] = useState<number | null>(null);
  /**
   * TWO INDEPENDENT CONTROLS, not one.
   *
   * `subject` — WHAT you are looking at. Chosen on the left: Benefit, ROI, Risk, Cost,
   * Timeline, or the opportunity's own Additional description. `null` when none is chosen.
   *
   * `view` — HOW you are looking at it. Chosen on the right: the business case, or the
   * full detail.
   *
   * They were one piece of state, and that was the bug the owner hit: the right panel's
   * "Full details" and the left panel's "Additional description" were wired to the same
   * key, so switching view un-selected his subject. They are different things that
   * happened to share a word. The left tile is the opportunity's extra sentences; the
   * right tab is the detail of whichever subject is selected — every one of the five
   * metrics has a detail view, and none of them is the additional description.
   */
  const [subject, setSubject] = useState<'desc' | SpnDimensionKey | null>(null);
  const [view, setView] = useState<'case' | 'details'>('case');

  /**
   * Picking a subject means "show me this", so it opens the detail. Picking the same one
   * again clears it and falls back to the business case, which is the resting state.
   */
  const pickSubject = useCallback((next: 'desc' | SpnDimensionKey) => {
    setSubject((prev) => {
      if (prev === next) {
        setView('case');
        return null;
      }
      setView('details');
      return next;
    });
  }, []);

  const [closedAreas, setClosedAreas] = useState<Set<string>>(new Set());
  /**
   * Opportunities whose variants are folded away, by the ORIGINAL's id.
   *
   * Expanded by default: a revision you just produced should be on screen, not behind a
   * click. Folding is for later, when an opportunity has three or four of them and you are
   * scanning the list for something else.
   */
  const [foldedVariants, setFoldedVariants] = useState<Set<number>>(new Set());

  /**
   * Opportunities whose detail is open, by id. EMPTY BY DEFAULT — everything starts
   * collapsed (owner's ruling, 2026-08-06).
   *
   * Tracked as what is OPEN rather than what is closed, so a run that returns two hundred
   * findings does not have to enumerate two hundred ids to say "all shut". The default
   * state costs nothing to represent.
   */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [steer, setSteer] = useState('');
  const [areaDraft, setAreaDraft] = useState('');
  const [pendingAreas, setPendingAreas] = useState<string[]>([]);
  const [steerOpen, setSteerOpen] = useState(false);

  /* ── challenging one opportunity ────────────────────────────────────────
     `draft` holds the client's edits before they are sent. It is seeded from the open
     opportunity when editing starts and thrown away on cancel, so a half-finished edit
     never reaches the database and never survives switching rows. */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [challenge, setChallenge] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [challenges, setChallenges] = useState<SpnChallenge[]>([]);

  /**
   * How tall the SCREEN is. One measurement, and the browser divides it.
   *
   * `.ds` is sized to the space actually available and made a flex column; the head takes
   * what it takes and the split takes the rest. Nothing below it measures anything, and
   * nothing scrolls except the two panes.
   *
   * The bottom allowance is READ, not guessed: the shell reserves the vendor footer's band
   * as padding on `<main>`, so its height is in the computed style. Every constant I put
   * here previously was wrong for the same reason — it described something the browser
   * already knew.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const [pageHeight, setPageHeight] = useState<string>('80vh');

  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      const main = el.closest('main');
      const reserved = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const top = el.getBoundingClientRect().top;
      setPageHeight(`${Math.max(420, Math.round(window.innerHeight - top - reserved - 8))}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (rootRef.current) ro.observe(rootRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, []);

  const load = useCallback(async () => {
    const data = await api.spn.getDiscoverySession(code);
    setSession(data.session);
    setOpportunities(data.opportunities);
    setRounds(data.rounds);
    setOpenId((prev) => prev ?? data.opportunities[0]?.id ?? null);
    // Keep polling while EITHER the session is generating or any single opportunity is
    // being re-run. Stopping on session status alone would leave a re-run spinning.
    const revising = data.opportunities.some((o) => o.revision_status === 'running');
    return data.session.status === 'running' || revising ? 'running' : data.session.status;
  }, [code]);

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    const tick = async () => {
      try {
        const status = await load();
        if (!alive) return;
        if (status === 'running') timer = window.setTimeout(tick, POLL_MS);
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    void tick();
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [load]);

  useEffect(() => {
    let alive = true;
    api.spn
      .getChallenges()
      .then((d) => { if (alive) setChallenges(d.challenges); })
      .catch(() => { /* the box still works without the library */ });
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => {
    const out: Array<{ area: string; items: SpnDiscoveryOpportunity[] }> = [];
    for (const o of opportunities) {
      const last = out[out.length - 1];
      if (last && last.area === o.business_area) last.items.push(o);
      else out.push({ area: o.business_area, items: [o] });
    }
    return out;
  }, [opportunities]);

  const open = useMemo(
    () => opportunities.find((o) => o.id === openId) ?? null,
    [opportunities, openId],
  );

  /* Optimistic on both writes below: ticking a box and picking a case are the whole
     interaction, and a round trip before the state moves makes a list of a hundred feel
     broken. The previous state is restored if the server refuses. */
  const select = async (o: SpnDiscoveryOpportunity, selected: boolean) => {
    const before = opportunities;
    setOpportunities((prev) => prev.map((x) => (x.id === o.id ? { ...x, selected } : x)));
    try {
      await api.spn.selectOpportunity(o.id, selected);
    } catch (e) {
      setOpportunities(before);
      setError((e as Error).message);
    }
  };

  const choose = async (o: SpnDiscoveryOpportunity, key: SpnDimensionKey, level: SpnCaseLevel) => {
    const before = opportunities;
    setOpportunities((prev) =>
      prev.map((x) => (x.id === o.id ? { ...x, chosen: { ...x.chosen, [key]: level } } : x)),
    );
    try {
      await api.spn.chooseCase(o.id, key, level);
    } catch (e) {
      setOpportunities(before);
      setError((e as Error).message);
    }
  };

  const selectArea = async (area: string | undefined, selected: boolean) => {
    setBusy(true);
    try {
      await api.spn.selectAll(code, { selected, ...(area ? { businessArea: area } : {}) });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Seed the draft from what is on screen, so editing starts from the current state. */
  const startEdit = (o: SpnDiscoveryOpportunity) => {
    setDraft({
      description: o.description,
      additional_comments: o.additional_comments ?? '',
      rationale: o.rationale ?? '',
      basis: o.basis ?? '',
      assumptions: o.assumptions ?? '',
      confidence_reason: o.confidence_reason ?? '',
      gating: o.gating ?? '',
      first_step: o.first_step ?? '',
      cases: JSON.parse(JSON.stringify(casesOf(o))),
    });
    setChallenge('');
    setChallengeId(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({});
    setChallenge('');
    setChallengeId(null);
  };

  /**
   * Save the edits, then start the re-run.
   *
   * Saved FIRST and separately: a re-run that fails must still leave the client's work on
   * the record rather than discarding it along with the failure.
   */
  const rerun = async (o: SpnDiscoveryOpportunity) => {
    setBusy(true);
    setError(undefined);
    try {
      const cases = draft.cases as Record<string, SpnDimension<string | number> | null>;
      const benefit = cases?.benefit;
      const money = (v: unknown) => {
        if (v == null) return null;
        // "$4.1m" / "$340k" / "4100000" — the numeric columns feed the tally, so they have
        // to track whatever the client typed into the benefit case.
        const text = String(v).trim().toLowerCase();
        const n = parseFloat(text.replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(n)) return null;
        if (text.includes('m')) return Math.round(n * 1_000_000);
        if (text.includes('k')) return Math.round(n * 1_000);
        return Math.round(n);
      };

      await api.spn.updateOpportunity(o.id, {
        ...(draft as Record<string, never>),
        ...(benefit
          ? {
              ebitda_low: money(benefit.low?.value),
              ebitda_mid: money(benefit.medium?.value),
              ebitda_high: money(benefit.high?.value),
            }
          : {}),
      });
      await api.spn.rerunOpportunity(o.id, {
        challenge,
        ...(challengeId ? { challengeId } : {}),
      });
      cancelEdit();
      await load();
      // Resume polling — the session may have settled long ago, so the effect's own loop
      // is not running.
      const poll = async () => {
        const status = await load();
        if (status === 'running') window.setTimeout(poll, POLL_MS);
      };
      window.setTimeout(poll, POLL_MS);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveOnly = async (o: SpnDiscoveryOpportunity) => {
    setBusy(true);
    try {
      await api.spn.updateOpportunity(o.id, draft as Record<string, never>);
      cancelEdit();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };



  const removeOpportunity = async (o: SpnDiscoveryOpportunity) => {
    const kids = opportunities.filter((x) => x.variant_of_id === o.id).length;
    if (
      !window.confirm(
        `Delete “${o.description}”?` +
          (kids ? ` Its ${kids} variant${kids === 1 ? '' : 's'} go with it.` : '') +
          ' This cannot be undone.',
      )
    )
      return;
    setBusy(true);
    try {
      await api.spn.deleteOpportunity(o.id);
      if (openId === o.id) setOpenId(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };


  /** Build and download. Scoped to THIS session, so a tick elsewhere is not swept in. */
  const exportAs = async (format: 'docx' | 'pptx') => {
    setBusy(true);
    setError(undefined);
    try {
      await api.spn.downloadExport(format, code);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const destroy = async () => {
    if (
      !window.confirm(
        `Delete ${session?.session_code}? Every opportunity, project and round in this ` +
          'discovery goes with it. This cannot be undone.',
      )
    )
      return;
    setBusy(true);
    try {
      await api.spn.deleteDiscovery(code);
      navigate('/spn/discoveries');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const addArea = () => {
    const value = areaDraft.trim();
    if (!value) return;
    if (!pendingAreas.some((a) => a.toLowerCase() === value.toLowerCase())) {
      setPendingAreas((prev) => [...prev, value]);
    }
    setAreaDraft('');
  };

  const sendSteer = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api.spn.steerDiscovery(code, { instruction: steer, areasAdded: pendingAreas });
      setSteer('');
      setPendingAreas([]);
      setSteerOpen(false);
      const poll = async () => {
        const status = await load();
        if (status === 'running') window.setTimeout(poll, POLL_MS);
      };
      await load();
      window.setTimeout(poll, POLL_MS);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="note">Loading…</p>;
  if (!session) {
    return (
      <div className="ds">
        <PageHeader title="Discovery not found" />
        <p style={{ color: '#b91c1c' }}>{error ?? `No session with the code ${code}.`}</p>
      </div>
    );
  }

  const isRunning = session.status === 'running';
  const selected = opportunities.filter((o) => o.selected);
  const totalIdentified = opportunities.reduce((a, o) => a + (Number(o.ebitda_impact) || 0), 0);
  const areaCount = (session.business_areas?.length ?? 0) + (session.custom_areas?.length ?? 0);

  return (
    <div className="ds" ref={rootRef} style={{ '--ds-page': pageHeight } as React.CSSProperties}>
      {/* No "Back to Discoveries" — the breadcrumb handles it. */}
      <PageHeader
        title="Strategic Discoveries"
        description={`${session.session_code} · ${session.company_name} · Round ${session.round_number} · ${opportunities.length} opportunities across ${areaCount} business areas`}
      />

      <HelpPanel help={SESSION_HELP} />

      {error ? <p style={{ color: '#b91c1c', fontSize: 12.5 }}>{error}</p> : null}

      {/* Title, tally and export stay put while the panels beneath them scroll — they are
          the answer to "what am I looking at and what have I chosen", and that question
          does not stop mattering once you scroll past them. */}
      <div className="ds-anchor">
      {/* ── statistical cards ─────────────────────────────────────────── */}
      <div className="ds-stats">
        <div className="ds-stat lead">
          <div className="k">Opportunities found</div>
          <div className="v">{opportunities.length}</div>
          <div className="s">
            {areaCount} business area{areaCount === 1 ? '' : 's'} · round {session.round_number}
          </div>
        </div>
        <div className="ds-stat">
          <div className="k">Selected</div>
          <div className="v">{selected.length}</div>
          <div className="s">of {opportunities.length}</div>
        </div>
        <div className="ds-stat">
          <div className="k">Value selected</div>
          <div className="v num">{compact(selected.reduce((a, o) => a + chosenValue(o), 0))}</div>
          <div className="s">annual EBITDA, chosen case</div>
        </div>
        <div className="ds-stat">
          <div className="k">Total identified</div>
          <div className="v num">{compact(totalIdentified)}</div>
          <div className="s">annual EBITDA, medium case</div>
        </div>
        <div className="ds-stat">
          <div className="k">Business units</div>
          <div className="v">{session.business_units?.length || 1}</div>
          <div className="s">
            {session.business_units?.length ? 'declared' : 'the company as a whole'}
          </div>
        </div>
      </div>

      {/* ── toolbar ───────────────────────────────────────────────────── */}
      <div className="ds-card ds-bar">
        {/* APR's own Word and PowerPoint buttons — Microsoft's product colours, 34px, from
            `apr/source-css/theme.css`. Two different Word buttons in one product would be
            worse than either choice. */}
        <button
          className="ds-rd-btn word"
          disabled={!selected.length || busy}
          onClick={() => exportAs('docx')}
          title={
            selected.length
              ? `Word document for the ${selected.length} selected`
              : 'Tick at least one opportunity'
          }
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3.5 1.5h6L13 5v9.5H3.5z" />
            <path d="M9.5 1.5V5H13" />
            <path d="M5.5 8.5l1 3 1.5-3 1.5 3 1-3" />
          </svg>
          Word
        </button>
        <button
          className="ds-rd-btn ppt"
          disabled={!selected.length || busy}
          onClick={() => exportAs('pptx')}
          title={
            selected.length
              ? `PowerPoint deck for the ${selected.length} selected`
              : 'Tick at least one opportunity'
          }
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="2.5" width="12" height="8.5" rx="1" />
            <path d="M8 11v3M5.5 14h5" />
            <circle cx="8" cy="6.75" r="2" />
          </svg>
          PowerPoint
        </button>
        <span className="note">
          {selected.length
            ? `${selected.length} opportunit${selected.length === 1 ? 'y' : 'ies'} selected`
            : 'Nothing selected'}
        </span>
        <span className="spacer" />
        <button
          className="ds-btn ghost sm"
          onClick={() => setSteerOpen(true)}
          disabled={busy}
          title="Add a round to the whole session — a follow-up question, or another business area"
        >
          ▶ Steer run
        </button>
        <button
          className="ds-btn quiet sm"
          onClick={() => selectArea(undefined, false)}
          disabled={busy || !selected.length}
        >
          Clear selection
        </button>
        <button className="ds-btn quiet sm" onClick={destroy} disabled={busy}>
          Delete this discovery
        </button>
      </div>
      </div>

      {isRunning ? (
        <div className="ds-card" style={{ padding: '14px 18px', marginBottom: 14 }}>
          <strong style={{ fontSize: 13 }}>Round {session.round_number} is running.</strong>{' '}
          <span className="note">
            One analysis per business area, three at a time. Several minutes, and it keeps
            going if you close the tab.
            {opportunities.length ? ` ${opportunities.length} back so far.` : ''}
          </span>
        </div>
      ) : null}

      {session.run_error ? (
        <div className="ds-card" style={{ padding: '14px 18px', marginBottom: 14, borderColor: '#f2d4d4' }}>
          <strong style={{ fontSize: 13 }}>
            {session.status === 'failed' ? 'This run did not finish.' : 'Some areas did not complete.'}
          </strong>{' '}
          <span style={{ fontSize: 12.5, color: '#9b2f2f' }}>{session.run_error}</span>
        </div>
      ) : null}

      {/* ── the split ─────────────────────────────────────────────────── */}
      <div className="ds-split">
        {/* Bounded by how many opportunities there are. */}
        <div className="ds-card ds-pane">
          <div className="ds-panehead">
            <span>Opportunities</span>
            <span className="spacer" />
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#9ca3af' }}>
              Tick to include · click a metric to open it
            </span>
          </div>

          <div className="ds-scroll">
          {opportunities.length === 0 ? (
            <p className="note" style={{ padding: '40px 16px', textAlign: 'center' }}>
              {isRunning ? 'Nothing back yet.' : 'This run returned no opportunities.'}
            </p>
          ) : (
            groups.map((group) => {
              const areaSelected = group.items.filter((o) => o.selected).length;
              const openGroup = !closedAreas.has(group.area);
              return (
                <div key={group.area}>
                  <div className="ds-area">
                    <input
                      type="checkbox"
                      checked={areaSelected === group.items.length}
                      ref={(el) => {
                        // Tri-state: a partial selection must not read as "none".
                        if (el) el.indeterminate = areaSelected > 0 && areaSelected < group.items.length;
                      }}
                      onChange={(e) => selectArea(group.area, e.target.checked)}
                    />
                    {/* A 24px bordered target, not a 13px glyph. The caret was invisible
                        and you had to guess where to click to expand an area. */}
                    <button
                      type="button"
                      className="ds-caret"
                      aria-expanded={openGroup}
                      aria-label={`${openGroup ? 'Collapse' : 'Expand'} ${group.area}`}
                      title={`${openGroup ? 'Collapse' : 'Expand'} ${group.area}`}
                      onClick={() =>
                        setClosedAreas((prev) => {
                          const next = new Set(prev);
                          if (!next.delete(group.area)) next.add(group.area);
                          return next;
                        })
                      }
                    >
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        {openGroup ? <path d="M3.5 6l4.5 4.5L12.5 6" /> : <path d="M6 3.5L10.5 8 6 12.5" />}
                      </svg>
                    </button>
                    <div>
                      <div className="nm">{group.area}</div>
                      <div className="ct">
                        {group.items.length} found ·{' '}
                        {compact(group.items.reduce((a, o) => a + (Number(o.ebitda_impact) || 0), 0))}{' '}
                        identified
                      </div>
                    </div>
                    <span className="spacer" />
                    {/* Per AREA, because that is the unit the list is grouped by — expanding
                        everything in a run at once is not a thing anyone wants on two
                        hundred findings. */}
                    <button
                      className="ds-btn quiet sm"
                      onClick={() =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          const allOpen = group.items.every((o) => next.has(o.id));
                          group.items.forEach((o) =>
                            allOpen ? next.delete(o.id) : next.add(o.id),
                          );
                          return next;
                        })
                      }
                      title={`Expand or collapse every opportunity in ${group.area}`}
                    >
                      {group.items.every((o) => expanded.has(o.id)) ? 'Collapse' : 'Expand'} all
                    </button>
                    <button
                      className="ds-btn quiet sm"
                      onClick={() => selectArea(group.area, true)}
                      disabled={busy}
                    >
                      Select all {group.items.length}
                    </button>
                  </div>

                  {openGroup
                    ? group.items
                        // A revision shows only when its original is EXPANDED and its
                        // revisions are not folded. Collapsing an opportunity now takes its
                        // revisions with it (owner's ruling, 2026-08-06) — they are part of
                        // that opportunity, and leaving three near-identical rows on screen
                        // after collapsing the thing they belong to reads as a bug.
                        .filter(
                          (o) =>
                            !o.variant_of_id ||
                            (expanded.has(o.variant_of_id) &&
                              !foldedVariants.has(o.variant_of_id)),
                        )
                        .map((o) => (
                        <OpportunityRow
                          key={o.id}
                          o={o}
                          variantCount={
                            o.variant_of_id
                              ? 0
                              : group.items.filter((x) => x.variant_of_id === o.id).length
                          }
                          variantsOpen={!foldedVariants.has(o.id)}
                          onToggleVariants={() =>
                            setFoldedVariants((prev) => {
                              const next = new Set(prev);
                              if (!next.delete(o.id)) next.add(o.id);
                              return next;
                            })
                          }
                          isOpen={o.id === openId}
                          expanded={expanded.has(o.id)}
                          onToggleExpanded={() =>
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (!next.delete(o.id)) next.add(o.id);
                              return next;
                            })
                          }
                          subject={subject}
                          onOpen={(k) => {
                            // A different opportunity starts fresh: its business case, with
                            // nothing carried over from the row you were on.
                            if (o.id !== openId) {
                              setOpenId(o.id);
                              cancelEdit();
                              if (k === null) {
                                setSubject(null);
                                setView('case');
                              } else {
                                setSubject(k);
                                setView('details');
                              }
                            } else if (k === null) {
                              setSubject(null);
                              setView('case');
                            } else {
                              pickSubject(k);
                            }
                          }}
                          onSelect={(v) => select(o, v)}
                          onDelete={() => removeOpportunity(o)}
                        />
                      ))
                    : null}
                </div>
              );
            })
          )}
          </div>
        </div>

        <div className="ds-rightwrap">
          {/* Bounded by how much the open lens has to say. Neither pane drags the other. */}
          <div className="ds-card ds-pane">
            {open ? (
              <>
                {/* ── the revision bar ────────────────────────────────────
                    Four states, and each says what the panel below it is for: reading,
                    editing, waiting on a re-run, or reviewing what came back. */}
                {open.revision_status === 'running' ? (
                  <div className="ds-editbar running">
                    <span className="k">
                      <span className="ds-spin" /> Re-running — same modules, same run
                      settings, this opportunity only
                    </span>
                  </div>
                ) : editing ? (
                  <div className="ds-editbar">
                    <span className="k">Editing — every value below is yours to change</span>
                    <span className="spacer" />
                    <button className="ds-btn quiet sm" onClick={cancelEdit} disabled={busy}>
                      Cancel
                    </button>
                    <button
                      className="ds-btn quiet sm"
                      onClick={() => saveOnly(open)}
                      disabled={busy}
                      title="Keep your edits without asking the model to reconsider"
                    >
                      Save only
                    </button>
                    <button
                      className="ds-btn sm"
                      onClick={() => rerun(open)}
                      disabled={busy}
                      title="Sends this back through the same modules. The result arrives as a variant beside it — this opportunity is not changed."
                    >
                      {busy ? 'Starting…' : 'Re-run as a variant'}
                    </button>
                  </div>
                ) : (
                  <div className="ds-editbar quiet">
                    <span className="k">
                      {open.variant_label ? `Variant · ${open.variant_label}` : 'As analysed'}
                    </span>
                    <span className="spacer" />
                    <button className="ds-btn ghost sm" onClick={() => startEdit(open)}>
                      Edit &amp; challenge
                    </button>
                  </div>
                )}

                {/* THE VIEW. Independent of the subject on the left — switching here
                    never changes what is selected there. */}
                <div className="ds-lenses1">
                  <button
                    className={`ds-lens-btn${view === 'case' ? ' on' : ''}`}
                    onClick={() => setView('case')}
                    title="The reasoning behind this opportunity"
                  >
                    <div className="k">Business case</div>
                    <div className="v">Why it is what it is</div>
                  </button>
                  <button
                    className={`ds-lens-btn${view === 'details' ? ' on' : ''}`}
                    onClick={() => setView((v) => (v === 'details' ? 'case' : 'details'))}
                    title={
                      subject
                        ? 'The detail of whatever is selected on the left'
                        : 'Pick something on the left first'
                    }
                  >
                    <div className="k">Full details</div>
                    <div className="v">
                      {subject
                        ? subject === 'desc'
                          ? 'Additional description'
                          : (METRICS.find((m) => m.key === subject)?.label ?? '')
                        : 'Nothing selected'}
                    </div>
                  </button>
                </div>

                {/* THE SUBJECT, mirrored from the left so both selectors agree. */}
                <div className="ds-lenses2">
                  {METRICS.map((m) => (
                    <button
                      key={m.key}
                      className={`ds-lens-btn${subject === m.key ? ' on' : ''}`}
                      onClick={() => pickSubject(m.key)}
                      title={
                        subject === m.key
                          ? `Turn ${m.label.toLowerCase()} off`
                          : `Turn ${m.label.toLowerCase()} on`
                      }
                    >
                      <div className="k">{m.label}</div>
                      <div className="v">
                        <span className={`ds-vl ${casesOf(open)[m.key]?.tone ?? 'moderate'}`}>
                          {displayValue(open, m.key)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="ds-lens ds-scroll">
                  <Lens
                    o={open}
                    subject={subject}
                    view={view}
                    onChoose={choose}
                    editing={editing}
                    draft={draft}
                    setDraft={setDraft}
                    challenge={challenge}
                    setChallenge={setChallenge}
                    challenges={challenges}
                    onPickChallenge={(c) => {
                      setChallenge(c.body);
                      setChallengeId(c.id);
                    }}
                  />

                  {/* THE VERDICT, at the FOOT of the panel (owner's ruling, 2026-08-05).
                      It was pinned above the tabs, which was wrong twice over: you read it
                      before the thing it is commenting on, and being outside the scrolling
                      body it ate the height the panel needed to scroll — a long verdict left
                      almost nothing to page through. Inside and last, it does neither. */}
                  {open.revision_error ? (
                    <div className="ds-verdict failed">
                      <div className="k">The re-run did not finish</div>
                      <p>{open.revision_error}</p>
                    </div>
                  ) : null}

                  {!editing && open.revision_verdict ? (
                    <div className="ds-verdict">
                      <div className="k">Its answer to your challenge</div>
                      {open.client_challenge ? (
                        <p className="asked">“{open.client_challenge}”</p>
                      ) : null}
                      <p>{open.revision_verdict}</p>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="note ds-scroll" style={{ padding: '50px 20px', textAlign: 'center' }}>
                Pick an opportunity on the left.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── steering ────────────────────────────────────────────────────
          A DIALOG, not a section below the panes (owner's ruling, 2026-08-05).
          Anything below the two panes makes the page scroll, and a page that scrolls
          under a sticky head is where the scroll trap lived. With this in a dialog the
          screen is exactly one viewport and the only scrolling is inside the panes. */}
      {steerOpen ? (
        <>
          <div className="ds-modal-scrim" onClick={() => setSteerOpen(false)} />
          <div className="ds-modal" role="dialog" aria-label="Steer this run">
            <div className="ds-modal-head">
              <strong>Steer this run</strong>
              <span className="spacer" />
              <button
                className="ds-btn quiet sm"
                onClick={() => setSteerOpen(false)}
                aria-label="Close"
              >
                Close
              </button>
            </div>
            <div className="ds-modal-body">
              <p className="note" style={{ margin: '0 0 14px' }}>
                Adds a round to the WHOLE session — a follow-up question, or another business
                area. Different from re-running one opportunity, which produces a variant.
                It is told what you have already selected, so it does not repeat itself.
              </p>

              <textarea
                value={steer}
                onChange={(e) => setSteer(e.target.value)}
                placeholder="e.g. You surfaced revenue cut-off, which I did not expect — keep going on that."
                style={{
                  width: '100%',
                  minHeight: 96,
                  font: 'inherit',
                  fontSize: 13,
                  padding: '9px 11px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 3,
                  resize: 'vertical',
                }}
              />

              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={areaDraft}
                  onChange={(e) => setAreaDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addArea();
                    }
                  }}
                  placeholder="Add a business area to this session"
                  style={{
                    font: 'inherit',
                    fontSize: 13,
                    padding: '6px 10px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 3,
                    minWidth: 280,
                  }}
                />
                <button className="ds-btn quiet sm" onClick={addArea}>
                  Add
                </button>
                {pendingAreas.map((a) => (
                  <span key={a} className="note">
                    {a}
                    <button
                      type="button"
                      aria-label={`Remove ${a}`}
                      onClick={() => setPendingAreas((prev) => prev.filter((x) => x !== a))}
                      style={{ border: 0, background: 'none', cursor: 'pointer', color: '#6b7280' }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              {rounds.length > 1 ? (
                <>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '.07em',
                      textTransform: 'uppercase',
                      color: '#6b7280',
                      margin: '22px 0 8px',
                    }}
                  >
                    Rounds so far
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <tbody>
                      {rounds.map((r) => (
                        <tr key={r.round_number} style={{ borderBottom: '1px solid #f0f2f5' }}>
                          <td style={{ padding: '8px 8px 8px 0', width: 50 }}>{r.round_number}</td>
                          <td style={{ padding: 8 }}>
                            {r.instruction ?? <span className="note">The initial analysis.</span>}
                            {r.areas_added?.length ? (
                              <div className="note">Added: {r.areas_added.join(', ')}</div>
                            ) : null}
                          </td>
                          <td style={{ padding: 8, width: 80 }} className="num">
                            {r.opportunities_returned ?? '—'}
                          </td>
                          <td style={{ padding: 8, width: 140 }} className="num">
                            {(r.input_tokens ?? 0).toLocaleString('en-US')} /{' '}
                            {(r.output_tokens ?? 0).toLocaleString('en-US')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}
            </div>

            <div className="ds-modal-foot">
              <span className="note">
                Round {session.round_number} · {selected.length} selected
              </span>
              <span className="spacer" />
              <button className="ds-btn quiet sm" onClick={() => setSteerOpen(false)}>
                Cancel
              </button>
              <button
                className="ds-btn"
                onClick={sendSteer}
                disabled={busy || isRunning || (!steer.trim() && !pendingAreas.length)}
                title={isRunning ? 'Wait for the current round to finish.' : 'Add a round.'}
              >
                {busy ? 'Sending…' : 'Ask & continue'}
              </button>
            </div>
          </div>
        </>
      ) : null}

    </div>
  );
}

/** One row in the left panel: title, unit, chosen value, and the six lens tiles. */
function OpportunityRow({
  o,
  isOpen,
  expanded,
  onToggleExpanded,
  subject,
  variantCount,
  variantsOpen,
  onToggleVariants,
  onOpen,
  onSelect,
  onDelete,
}: {
  o: SpnDiscoveryOpportunity;
  isOpen: boolean;
  /** Is the detail open? Collapsed by default — the tiles are most of a row's height. */
  expanded: boolean;
  onToggleExpanded: () => void;
  /** How many revisions this opportunity has. 0 on a variant — they do not nest. */
  variantCount: number;
  variantsOpen: boolean;
  onToggleVariants: () => void;
  /** What is selected. `null` when nothing is. */
  subject: 'desc' | SpnDimensionKey | null;
  /** `null` means the title was clicked — clear the subject. */
  onOpen: (subject: 'desc' | SpnDimensionKey | null) => void;
  onSelect: (selected: boolean) => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={
        `ds-opp${isOpen ? ' on' : ''}` +
        // A revision GROUP: the opportunity that has revisions, and the revisions
        // themselves. Both carry the same rule so the group reads as one block.
        `${o.variant_of_id || variantCount > 0 ? ' grouped' : ''}` +
        `${o.variant_of_id ? ' variant' : ''}`
      }
    >
      <input type="checkbox" checked={o.selected} onChange={(e) => onSelect(e.target.checked)} />
      {/* THE SAME CONTROL THE BUSINESS AREA USES — `ds-caret`, 24px, bordered, an SVG
          chevron. It was a bare 11px glyph, which is the mistake this screen has already
          made twice: a control sized like a caption is one nobody finds.
          Its own column, so every title starts at the same x whether the row is open or
          shut; inline, the text shifted as it toggled. A variant has no control of its own
          — it is inside the group its original opens. */}
      {o.variant_of_id ? (
        <span aria-hidden />
      ) : (
        <button
          type="button"
          className="ds-caret"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${o.description}`}
          title={expanded ? 'Collapse this opportunity' : 'Expand this opportunity'}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            {expanded ? <path d="M3.5 6l4.5 4.5L12.5 6" /> : <path d="M6 3.5L10.5 8 6 12.5" />}
          </svg>
        </button>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="hd">
          <div style={{ minWidth: 0 }}>
            <button type="button" className="d" onClick={() => onOpen(null)}>
              {o.description}
            </button>
            {o.variant_label ? <span className="ds-variant-tag">{o.variant_label}</span> : null}
            <div className="u">
              {o.business_unit || 'the company as a whole'}
              {o.is_unexpected ? ' · unexpected' : ''}
            </div>
            {/* On EVERY opportunity, not only the ones that happen to have revisions
                (owner's ruling, 2026-08-05) — a control that appears and disappears is one
                nobody learns is there. At zero it is inert and says what would create one.
                Not shown on a variant: revisions do not nest. */}
            {expanded && !o.variant_of_id ? (
              <button
                type="button"
                className={`ds-varfold${variantCount === 0 ? ' empty' : ''}`}
                onClick={variantCount > 0 ? onToggleVariants : undefined}
                disabled={variantCount === 0}
                aria-expanded={variantCount > 0 ? variantsOpen : undefined}
                title={
                  variantCount === 0
                    ? 'No revisions yet — Edit & challenge, then Re-run as a variant'
                    : variantsOpen
                      ? 'Hide the revisions'
                      : 'Show the revisions'
                }
              >
                <span className="caret">
                  {variantCount === 0 ? '·' : variantsOpen ? '▾' : '▸'}
                </span>
                {variantCount} revision{variantCount === 1 ? '' : 's'}
              </button>
            ) : null}
          </div>
          <div className="val">
            <b className="num">{displayValue(o, 'benefit')}</b>
            <div>a year, {o.chosen?.benefit ?? 'medium'}</div>
          </div>
          {/* On hover, so a list of a hundred is not a wall of buttons. */}
          {/* No "+ Variant". A re-run IS how a variant comes into existence (owner's
              ruling, 2026-08-05) — a copy nobody has argued with is just a duplicate. */}
          <div className="rowacts">
            <button type="button" className="danger" onClick={onDelete} title="Delete this opportunity">
              Delete
            </button>
          </div>
        </div>

        {expanded ? (
        <div className="ds-tiles">
          <button
            className={`ds-tile${isOpen && subject === 'desc' ? ' on' : ''}`}
            onClick={() => onOpen('desc')}
          >
            {ICON.desc}
            <span className="lb">Additional description</span>
          </button>
          {METRICS.map((m) => (
            <button
              key={m.key}
              className={`ds-tile${isOpen && subject === m.key ? ' on' : ''}`}
              onClick={() => onOpen(m.key)}
            >
              {ICON[m.key]}
              <span className="lb">{m.label}</span>
              <span className={`ds-vl ${casesOf(o)[m.key]?.tone ?? 'moderate'}`}>
                {displayValue(o, m.key)}
              </span>
            </button>
          ))}
        </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The right panel.
 *
 * In EDIT mode every field becomes an input backed by `draft` — including all three values
 * and all three justifications on each dimension. The draft is the client's POSITION: facts
 * they are asserting. The challenge box below carries the ARGUMENT. They are separate
 * because edits alone are numbers with no reasoning, and a challenge alone is a claim with
 * no numbers; the model gets both.
 */
function Lens({
  o,
  subject,
  view,
  onChoose,
  editing,
  draft,
  setDraft,
  challenge,
  setChallenge,
  challenges,
  onPickChallenge,
}: {
  o: SpnDiscoveryOpportunity;
  /** WHAT is being looked at. */
  subject: 'desc' | SpnDimensionKey | null;
  /** HOW — the reasoning, or the detail. */
  view: 'case' | 'details';
  onChoose: (o: SpnDiscoveryOpportunity, key: SpnDimensionKey, level: SpnCaseLevel) => void;
  editing: boolean;
  draft: Record<string, unknown>;
  setDraft: (fn: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  challenge: string;
  setChallenge: (v: string) => void;
  challenges: SpnChallenge[];
  onPickChallenge: (c: SpnChallenge) => void;
}) {
  /** A plain narrative field: read-only text, or a textarea bound to the draft. */
  const text = (key: string, label: string, value: string | null, rows = 3) =>
    editing ? (
      <div className="ds-fld" key={key}>
        <div className="k">{label}</div>
        <textarea
          className="ds-f"
          rows={rows}
          value={String(draft[key] ?? '')}
          onChange={(e) => setDraft((p) => ({ ...p, [key]: e.target.value }))}
        />
      </div>
    ) : (
      <Field label={label} key={key}>
        {value}
      </Field>
    );

  /** Edit a dimension's own prose — `cases.timeline.narrative`. Not per-level. */
  const setNarrative = (key: SpnDimensionKey, next: string) =>
    setDraft((p) => {
      const cases = JSON.parse(JSON.stringify(p.cases ?? {}));
      cases[key] ??= { tone: 'moderate', low: {}, medium: {}, high: {} };
      cases[key].narrative = next;
      return { ...p, cases };
    });

  /** Edit one cell of one case — `cases.benefit.medium.value`, and its justification. */
  const setCase = (
    key: SpnDimensionKey,
    level: SpnCaseLevel,
    field: 'value' | 'justification',
    next: string,
  ) =>
    setDraft((p) => {
      const cases = JSON.parse(JSON.stringify(p.cases ?? {}));
      cases[key] ??= { tone: 'moderate', low: {}, medium: {}, high: {} };
      cases[key][level] ??= {};
      // Timeline is stored as a number of weeks; everything else is a formatted string.
      cases[key][level][field] =
        field === 'value' && key === 'timeline'
          ? (Number.isFinite(parseInt(next, 10)) ? parseInt(next, 10) : null)
          : next;
      return { ...p, cases };
    });

  const challengeBox = editing ? (
    <div className="ds-chal">
      <div className="k">What do you want tested?</div>
      <p className="note" style={{ margin: '0 0 8px' }}>
        Your edits above are facts you are asserting. This is the argument — what you believe
        and what you want checked. Both go back with the same modules and run settings.
      </p>
      <textarea
        className="ds-f"
        rows={4}
        value={challenge}
        onChange={(e) => setChallenge(e.target.value)}
        placeholder="e.g. Our own AP audit measured 0.18%, not the 0.22% you assumed — apply our rate throughout, and check whether there is justification to go lower still."
      />
      {challenges.length ? (
        <>
          <div className="note" style={{ marginTop: 10, marginBottom: 4 }}>
            Saved challenges
          </div>
          <div className="ds-presets">
            {challenges.map((c) => (
              <button key={c.id} type="button" title={c.body} onClick={() => onPickChallenge(c)}>
                {c.name}
              </button>
            ))}
          </div>
        </>
      ) : null}
      <div className="note" style={{ marginTop: 10 }}>
        Re-runs this opportunity only, not the session. Roughly 30 seconds.
      </div>
    </div>
  ) : null;

  /* The BUSINESS CASE is the opportunity's, not the subject's — the reasoning behind the
     whole finding, which is what every subject is read against. */
  if (view === 'case') {
    return (
      <>
        {editing ? (
          <input
            className="ds-f title"
            value={String(draft.description ?? '')}
            onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
          />
        ) : (
          <h3>{o.description}</h3>
        )}
        <p className="lead">
          {o.business_unit || 'the company as a whole'} · {o.business_area}
        </p>
        {text('rationale', 'What is leaking', o.rationale)}
        {text('basis', 'How the figure was reached', o.basis)}
        {text('assumptions', 'What had to be assumed', o.assumptions)}
        {text('confidence_reason', `Confidence — ${o.confidence ?? 'unstated'}`, o.confidence_reason)}
        {text('gating', 'External gates', o.gating)}
        {text('first_step', 'First step', o.first_step, 2)}
        {challengeBox}
      </>
    );
  }

  /* Nothing chosen on the left, so there is no detail to show. */
  if (!subject) {
    return (
      <>
        <h3>Full details</h3>
        <p className="lead">
          Pick a category on the left — Additional description, Benefit, ROI, Risk, Cost or
          Timeline — and its detail appears here.
        </p>
        {challengeBox}
      </>
    );
  }

  /* The opportunity's own extra sentences. A subject like any other, and NOT the same
     thing as the per-dimension narrative further down. */
  if (subject === 'desc') {
    return (
      <>
        <h3>Additional description</h3>
        <p className="lead">{o.description}</p>
        {text('additional_comments', '', o.additional_comments, 10)}
        {challengeBox}
      </>
    );
  }

  const lens = subject;

  const dim = editing
    ? ((draft.cases as Record<string, SpnDimension<string | number>>)?.[lens] ??
       casesOf(o)[lens])
    : casesOf(o)[lens];
  const legacy = !o.cases;
  if (!dim) {
    return (
      <>
        <h3>{TITLE[lens]}</h3>
        <p className="lead">
          This opportunity predates the three-case analysis, and nothing in the older data
          stands in for {TITLE[lens].toLowerCase()}. Run the discovery again to get it.
        </p>
      </>
    );
  }

  const chosen = o.chosen?.[lens] ?? 'medium';

  return (
    <>
      <h3>{TITLE[lens]}</h3>
      {lens === 'timeline' ? (
        <>
          <p className="lead">
            Implementation effort across the five delivery phases — the work to make the
            change, not the time for the benefit to accrue. Pick the case you want to carry.
          </p>
          <Gantt o={o} dim={dim as SpnDimension<number>} chosen={chosen} onChoose={onChoose} />
          <div style={{ marginTop: 18 }}>
            <Field label="External gates">{o.gating}</Field>
          </div>
        </>
      ) : (
        <p className="lead">
          Three scopes and what each would take — {UNIT[lens]}. Medium is the recommendation;
          take another if it suits.
        </p>
      )}

      {legacy ? (
        <p className="lead" style={{ marginTop: -8 }}>
          Lifted from an older run, which recorded one assessment rather than three scopes.
          The justifications are blank because none were captured — re-run the discovery to
          get them.
        </p>
      ) : null}

      {/* THIS DIMENSION'S OWN PROSE. What drives the timeline, what the benefit rests on —
          about this dimension and nothing else. Not the opportunity's additional
          description, which is its own subject on the left. */}
      {editing ? (
        <div className="ds-fld">
          <div className="k">About the {TITLE[lens].toLowerCase()}</div>
          <textarea
            className="ds-f"
            rows={4}
            value={String(dim.narrative ?? '')}
            onChange={(e) => setNarrative(lens, e.target.value)}
            placeholder={`Two or three sentences about the ${TITLE[lens].toLowerCase()} for this opportunity.`}
          />
        </div>
      ) : dim.narrative ? (
        <div className="ds-narrative">{dim.narrative}</div>
      ) : (
        <p className="note" style={{ marginBottom: 14 }}>
          No commentary on the {TITLE[lens].toLowerCase()} was captured for this
          opportunity. Re-running it will produce one.
        </p>
      )}

      <div className="ds-levels">
        {LEVELS.map((level) => {
          const on = level === chosen;
          return (
            <button
              key={level}
              type="button"
              className={`ds-lv${on ? ` chosen ${dim.tone ?? 'moderate'}` : ''}`}
              onClick={() => onChoose(o, lens, level)}
            >
              <span>
                <input type="radio" checked={on} readOnly />
              </span>
              <span>
                <span className="t" style={{ display: 'block' }}>
                  {level}
                </span>
                {level === 'medium' ? <span className="tag">recommended</span> : null}
              </span>
              {editing ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <input
                    className="ds-f n"
                    value={String(dim[level]?.value ?? '')}
                    onChange={(e) => setCase(lens, level, 'value', e.target.value)}
                    aria-label={`${lens} ${level} value`}
                  />
                </span>
              ) : (
                <span className="n">{levelValue(dim, level, lens)}</span>
              )}
              {editing ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <textarea
                    className="ds-f"
                    rows={2}
                    value={String(dim[level]?.justification ?? '')}
                    onChange={(e) => setCase(lens, level, 'justification', e.target.value)}
                    aria-label={`${lens} ${level} justification`}
                  />
                </span>
              ) : (
                <span className="j">{dim[level]?.justification || '—'}</span>
              )}
            </button>
          );
        })}
      </div>
      {challengeBox}
    </>
  );
}

/**
 * Three Gantt charts on ONE shared axis, scaled to the longest case.
 *
 * Sharing the axis is the point — the reader is comparing how much longer the high case
 * takes, and three independently-scaled charts would make them all look the same length.
 */
function Gantt({
  o,
  dim,
  chosen,
  onChoose,
}: {
  o: SpnDiscoveryOpportunity;
  dim: SpnDimension<number>;
  chosen: SpnCaseLevel;
  onChoose: (o: SpnDiscoveryOpportunity, key: SpnDimensionKey, level: SpnCaseLevel) => void;
}) {
  const max = Math.max(1, Number(dim.high?.value) || Number(dim.medium?.value) || 1);
  const step = max <= 14 ? 2 : max <= 32 ? 4 : 8;
  const marks: number[] = [];
  for (let t = 0; t <= max; t += step) marks.push(t);

  return (
    <>
      <div className="ds-gantt">
        <div className="ds-axis">
          <div />
          <div />
          <div className="ds-ruler">
            {marks.map((t) => (
              <span key={t} style={{ left: `${(t / max) * 100}%` }}>
                {t}
              </span>
            ))}
          </div>
          <div style={{ textAlign: 'right', fontSize: 9.5, color: '#9ca3af' }}>weeks</div>
        </div>

        {LEVELS.map((level) => {
          const total = Number(dim[level]?.value) || 0;
          // Minimum one week per phase: a five-phase programme cannot have a zero-length
          // phase, and a zero-width segment would silently vanish from the bar.
          const weeks = PHASES.map((p) => Math.max(1, Math.round((total * p.ratio) / RATIO_TOTAL)));
          let run = 0;
          return (
            <button
              key={level}
              type="button"
              className={`ds-gr${level === chosen ? ' chosen' : ''}`}
              onClick={() => onChoose(o, 'timeline', level)}
            >
              <span>
                <input type="radio" checked={level === chosen} readOnly />
              </span>
              <span>
                <span className="t" style={{ display: 'block' }}>
                  {level}
                </span>
              </span>
              <span className="ds-track">
                {PHASES.map((p, i) => {
                  const left = (run / max) * 100;
                  const width = (weeks[i]! / max) * 100;
                  run += weeks[i]!;
                  return (
                    <span
                      key={p.name}
                      className="seg"
                      style={{ left: `${left}%`, width: `${width}%`, background: p.colour }}
                      title={`${p.name} — ${weeks[i]} wk`}
                    >
                      {width > 9 ? p.abbr : ''}
                    </span>
                  );
                })}
              </span>
              <span className="tot num">{total ? `${total} wk` : '—'}</span>
            </button>
          );
        })}
      </div>

      <div className="ds-legend">
        {PHASES.map((p) => (
          <div key={p.name}>
            <i style={{ background: p.colour }} />
            {p.name}
          </div>
        ))}
      </div>
    </>
  );
}

function Field({ label, children }: { label?: string; children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className="ds-fld">
      {label ? <div className="k">{label}</div> : null}
      <div className="v">{children}</div>
    </div>
  );
}
